import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OrdersSearchInputProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

/**
 * Collapsible search: shows only a search icon-button by default, expands into
 * a full input on click/focus. Stays open while it holds a value or is focused.
 */
export function OrdersSearchInput({ value, onChange }: OrdersSearchInputProps) {
  const hasValue = Boolean(value);
  const [open, setOpen] = useState(hasValue);
  const containerRef = useRef<HTMLDivElement>(null);

  const isExpanded = open || hasValue;

  const focusInput = () => {
    containerRef.current?.querySelector('input')?.focus();
  };

  useEffect(() => {
    if (open) {
      focusInput();
    }
  }, [open]);

  // Collapse on outside click when there's no active query
  useEffect(() => {
    if (!isExpanded || hasValue) return;
    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isExpanded, hasValue]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex items-center transition-[width] duration-200 ease-out',
        isExpanded ? 'w-[260px]' : 'w-8',
      )}
    >
      {isExpanded ? (
        <>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Поиск (ID, имя, email)"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            onBlur={() => {
              if (!hasValue) setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                onChange(undefined);
                setOpen(false);
              }
            }}
            className="h-8 w-full pl-8 pr-8 text-sm"
          />
          {hasValue && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => {
                onChange(undefined);
                focusInput();
              }}
              aria-label="Очистить поиск"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setOpen(true)}
          aria-label="Поиск"
          title="Поиск"
        >
          <Search className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
